import { Component, Input } from '@angular/core';
import { openClose } from '@core/animations/animations';
import {
	InsuranceAmountDescription,
	InterestCalculatedOptions,
	InterestRateTypeDescription,
} from '@core/util/mortgage';
import { isSet } from '@core/util/value';
import {
	InsuranceAmount,
	InterestRateType,
	MatterMortgage,
	MortgageInterestCalculations,
} from '@models/mortgage';
import { MatterMortgageDispatch } from '@state/matter/matter_mortgages';
import {
	DropdownOption,
	DropdownResult,
} from 'src/app/shared/atomic/atoms/dropdown-options/dropdown-options.component';
import { RadioGroupItem } from 'src/app/shared/atomic/atoms/radio-group/radio-group.component';
import { ICON } from '@core/util/icon';

const InsuranceAmountOptions: RadioGroupItem<InsuranceAmount>[] = [
	{
		label: InsuranceAmountDescription[InsuranceAmount.FULL],
		value: InsuranceAmount.FULL,
	},
	{
		label: InsuranceAmountDescription[InsuranceAmount.STANDARD_CHARGE],
		value: InsuranceAmount.STANDARD_CHARGE,
	},
];

const InterestRateTypeOptions: RadioGroupItem<InterestRateType>[] = [
	{
		label: InterestRateTypeDescription[InterestRateType.FIXED],
		value: InterestRateType.FIXED,
	},
	{
		label: InterestRateTypeDescription[InterestRateType.VARIABLE],
		value: InterestRateType.VARIABLE,
	},
];

@Component({
	selector: 'app-manual-mortgage-terms',
	templateUrl: './mortgage-terms.component.html',
	animations: [openClose()],
	standalone: false,
})
export class ManualMortgageTermsComponent {
	public ICON = ICON;
	public insuranceAmountOptions = InsuranceAmountOptions;
	public interestRateTypeOptions = InterestRateTypeOptions;
	public interestCalculatedOptions = InterestCalculatedOptions;
	public insuranceAmount: InsuranceAmount;
	public _mortgage: MatterMortgage;

	public overrideInterestForReg = false;

	@Input() set mortgage(mortgage: MatterMortgage) {
		this._mortgage = mortgage;
		if (mortgage) {
			if (
				!this.overrideInterestForReg &&
				(isSet(mortgage?.interestRateAsReg) ||
					isSet(mortgage?.interestRateTypeAsReg) ||
					isSet(mortgage?.interestCalculatedAsReg))
			) {
				this.overrideInterestForReg = true;
			}
		}
	}

	constructor(private mortgageDispatch: MatterMortgageDispatch) {}

	setAmount(value: number) {
		this.mortgageDispatch.updateMortgage(this._mortgage.id, { amount: value });
	}

	setInterestOnlyPayments(value: boolean) {
		this.mortgageDispatch.updateMortgage(this._mortgage.id, {
			interestOnlyPayments: value,
		});
	}

	setGeneralSecurityAgreement(value: boolean) {
		this.mortgageDispatch.updateMortgage(this._mortgage.id, {
			generalSecurityAgreement: value,
		});
	}

	setAssignmentOfRents(value: boolean) {
		this.mortgageDispatch.updateMortgage(this._mortgage.id, {
			assignmentOfRents: value,
		});
	}

	setStandardChargeTerm(value: string) {
		this.mortgageDispatch.updateMortgage(this._mortgage.id, {
			standardChargeTerm: value,
		});
	}

	setInsuranceAmount(value: InsuranceAmount) {
		this.mortgageDispatch.updateMortgage(this._mortgage.id, {
			insuranceAmount: value,
		});
	}

	setTerm(value: string) {
		this.mortgageDispatch.updateMortgage(this._mortgage.id, { term: value });
	}

	setOverrideInterestForEReg(value: boolean) {
		this.overrideInterestForReg = value;

		if (!value) {
			this.mortgageDispatch.updateMortgage(this._mortgage.id, {
				interestRateAsReg: '',
				interestRateTypeAsReg: '',
				interestCalculatedAsReg: '',
			});
		}
	}

	setInterestRate(value: number) {
		this.mortgageDispatch.updateMortgage(this._mortgage.id, {
			interestRate: value,
		});
	}

	setInterestRateType(value: InterestRateType) {
		this.mortgageDispatch.updateMortgage(this._mortgage.id, {
			interestRateType: value,
		});
	}

	setInterestCalculated(value: MortgageInterestCalculations) {
		this.mortgageDispatch.updateMortgage(this._mortgage.id, {
			interestCalculated: value,
		});
	}

	selectInterestCalculated(
		value: DropdownResult<DropdownOption<MortgageInterestCalculations>>,
	) {
		if (value?.result?.value) {
			this.setInterestCalculated(value.result.value);
		}
	}

	setRateAsReg(value: string) {
		this.mortgageDispatch.updateMortgage(this._mortgage.id, {
			interestRateAsReg: value,
		});
	}

	setRateTypeAsReg(value: string) {
		this.mortgageDispatch.updateMortgage(this._mortgage.id, {
			interestRateTypeAsReg: value,
		});
	}

	setInterestCalculatedAsReg(value: string) {
		this.mortgageDispatch.updateMortgage(this._mortgage.id, {
			interestCalculatedAsReg: value,
		});
	}
}
